// Shared labels and formatting for the code-review pages.  The string constants mirror the enums in
// Code/CodeReview/CodeReviewModels.cs, which Controllers/CodeReviewData.cs sends by name.

export const severities = ['Critical', 'High', 'Medium', 'Low'];

export const findingStatuses = ['Open', 'FixPushed', 'ApprovedBatch', 'ApprovedImmediate', 'FixInProgress', 'Fixed', 'Rejected', 'Superseded'];

export const defaultQueueStatuses = ['Open', 'FixPushed'];

/** FindingCategory names; Security comes first because the queue highlights it. */
export const categories = ['Security', 'Correctness', 'DataIntegrity', 'BuildDeploy', 'Performance', 'Maintainability', 'Documentation', 'Style'];

/** Handle and border colors of slide buttons, matching the border colors of the .crAction kinds in codeReview.css. */
export const slideColors = {
	plain: '#5A5A5A',
	danger: '#B02A37',
	positive: '#146C43',
	caution: '#997404',
	assist: '#2B5797'
};

export const targetStatuses = ['Pending', 'Reviewing', 'Triaging', 'Done', 'Failed', 'Safety', 'Skipped'];

export const fixBatchStatuses = ['Queued', 'Running', 'AwaitingPushApproval', 'Pushing', 'Pushed', 'NothingToPush', 'Failed', 'Discarded', 'Safety'];

export const rejectReasons = [
	{ value: 'InvalidFinding', label: 'Invalid finding' },
	{ value: 'BehaviorIsIntentional', label: 'Behavior is intentional' },
	{ value: 'NotWorthFixing', label: 'Not worth fixing' },
	{ value: 'Duplicate', label: 'Duplicate' },
	{ value: 'Other', label: 'Other' }
];

const labels = {
	FixPushed: 'Fix pushed',
	ApprovedBatch: 'Approved (batch)',
	ApprovedImmediate: 'Approved (immediate)',
	FixInProgress: 'Fix in progress',
	AlreadyFixed: 'Already fixed',
	InvalidFinding: 'Invalid finding',
	BehaviorIsIntentional: 'Behavior is intentional',
	NotWorthFixing: 'Not worth fixing',
	DesignDecision: 'Design decision',
	DataIntegrity: 'Data integrity',
	BuildDeploy: 'Build / deploy',
	CommentAdded: 'Comment added',
	CommentEdited: 'Comment edited',
	StatusChanged: 'Status changed',
	SeverityChanged: 'Severity changed',
	RepeatSeen: 'Raised again',
	ActionSuggested: 'Action suggested',
	ActionAccepted: 'Action accepted',
	ActionDeclined: 'Action declined',
	CommitLinked: 'Commit linked',
	PromptGenerated: 'Fix prompt generated',
	ReEvaluationRequested: 'Re-evaluation requested',
	ReEvaluationFailed: 'Re-evaluation failed',
	ReEvaluationCanceled: 'Re-evaluation canceled',
	ReEvaluate: 'Re-evaluate',
	BatchFix: 'Batch fix',
	AwaitingPushApproval: 'Awaiting push approval',
	NothingToPush: 'Nothing to push',
	fixed: 'Fixed',
	already_fixed: 'Already fixed',
	not_fixed: 'Not fixed',
	invalid: 'Invalid',
	skipped: 'Skipped'
};

/** Human label for an enum name sent by the server. */
export function label(name)
{
	if (name === null || name === undefined || name === '')
		return '';
	return labels[name] || name;
}

/** CSS class of a severity badge (see the .sev* classes in each page). */
export function severityClass(severity)
{
	return 'sev' + (severity || 'Low');
}

/** CSS class of a status badge for findings and pushes. */
export function statusClass(status)
{
	switch (status)
	{
		case 'Failed': return 'stFailed';
		case 'Safety': return 'stSafety';
		case 'Open':
		case 'AwaitingPushApproval':
			return 'stOpen';
		case 'Pushing':
			return 'stProgress';
		case 'Pushed':
		case 'fixed':
			return 'stDone';
		case 'not_fixed':
		case 'skipped':
			return 'stNeutral';
		case 'already_fixed':
		case 'invalid':
			return 'stOpen';
		case 'FixPushed':
		case 'ApprovedBatch':
		case 'ApprovedImmediate':
		case 'FixInProgress':
		case 'Pending':
		case 'Reviewing':
		case 'Triaging':
			return 'stProgress';
		case 'Queued':
		case 'Running':
			return 'stProgress';
		case 'Fixed':
		case 'Done':
		case 'Ok':
			return 'stDone';
		default:
			return 'stNeutral';
	}
}

function pad(n)
{
	return n < 10 ? '0' + n : '' + n;
}

/** Local time "YYYY-MM-DD HH:mm" from epoch milliseconds, or "—". */
export function formatTime(ms)
{
	if (!ms)
		return '—';
	let d = new Date(ms);
	return d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate()) + ' ' + pad(d.getHours()) + ':' + pad(d.getMinutes());
}

/** Local date "YYYY-MM-DD" from epoch milliseconds, or "—". */
export function formatDate(ms)
{
	if (!ms)
		return '—';
	let d = new Date(ms);
	return d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate());
}

/** "1m 23s" style duration from milliseconds, or "—". */
export function formatDurationMs(ms)
{
	if (ms === null || ms === undefined)
		return '—';
	let s = Math.round(ms / 1000);
	if (s < 60)
		return s + 's';
	let m = Math.floor(s / 60);
	s = s % 60;
	if (m < 60)
		return m + 'm ' + pad(s) + 's';
	return Math.floor(m / 60) + 'h ' + pad(m % 60) + 'm';
}

/** "$0.92" from a USD amount, or "—". */
export function formatCost(usd)
{
	if (usd === null || usd === undefined)
		return '—';
	return '$' + usd.toFixed(2);
}

/** Thousands-separated integer. */
export function formatCount(n)
{
	if (n === null || n === undefined)
		return '—';
	return n.toLocaleString();
}

/** The first 10 characters of a SHA. */
export function shortSha(sha)
{
	return sha && sha.length > 10 ? sha.substr(0, 10) : (sha || '');
}

/** "file:line" or "file". */
export function location(f)
{
	if (!f || !f.file)
		return '';
	return f.file + (f.line ? ':' + f.line : '');
}

/** Reads a JSON value from localStorage; null when missing, unreadable or blocked. */
export function loadStored(key)
{
	try
	{
		let s = window.localStorage.getItem(key);
		return s ? JSON.parse(s) : null;
	}
	catch (ex)
	{
		return null;
	}
}

/** Writes a JSON value to localStorage, ignoring failures (private windows, blocked storage). */
export function saveStored(key, value)
{
	try
	{
		window.localStorage.setItem(key, JSON.stringify(value));
	}
	catch (ex)
	{
	}
}
